import { Picker } from 'emoji-mart';
import 'emoji-mart/css/emoji-mart.css';
import { useTranslation } from 'next-i18next';

import React, { useRef, useState } from 'react';

import { Icon, Smiley } from '@thinkific/chalk/react-icons';
import { Popover } from '@thinkific/chalk/recipes/Popover';

import getDictionary from 'components/EmojiPicker/EmojiPickerDictionary';

import ActionButton from '../../ActionButton';
import * as css from './EmojiPicker.css';

interface EmojiPickerProps {
  icon: Icon;
  disabled?: boolean;
  onClick?: () => void;
  onSelect?: (emoji: string) => void;
}

const EmojiPicker = ({ icon = Smiley, disabled, onClick, onSelect }: EmojiPickerProps) => {
  const emojiRef = useRef<HTMLButtonElement>(null);
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const handleSelect = (emoji) => {
    emojiRef.current?.click();
    const { native } = emoji;
    if (native) {
      setIsOpen(false);
      onSelect?.(native);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    onClick?.();
  };

  return (
    <div className={css.emojiPicker} data-qa="emoji-picker">
      <Popover
        initialFocusRef={initialFocusRef}
        title={t('components-documentEditor-menuBar_emojiPicker', 'Add emoji')}
        trigger={
          <ActionButton
            arialLabel={t('Emoji')}
            disabled={disabled || false}
            icon={icon}
            isActive={isOpen}
            ref={emojiRef}
            onClick={handleOpen}
          />
        }
        triggerRef={emojiRef}
        hiddenTitle
      >
        <div
          role="presentation"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <button
            aria-label="Emoji picker focus target"
            ref={initialFocusRef}
            style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px',
              opacity: 0,
              overflow: 'hidden',
            }}
            tabIndex={0}
            type="button"
          />
          <Picker
            i18n={getDictionary(t)}
            showPreview={false}
            showSkinTones={false}
            emojiTooltip
            native
            onSelect={handleSelect}
          />
        </div>
      </Popover>
    </div>
  );
};

export default EmojiPicker;
